import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import ReactPlayerLoader from '@brightcove/react-player-loader';

const StyledVideoPlayerWrapper = styled.div`
  padding: 2rem 0;
  margin: 0 auto;
  .headline {
    text-align: center;
    padding: 2rem 1.5rem;
  }
  .player_container {
    position: relative;
    width: calc(100% - 2rem);
    max-width: 500px;
    margin: 0 auto 12rem;
    @media (min-width: 430px) {
      margin-bottom: 19rem;
    }
  }
  .brightcove-react-player-loader {
    .video-js {
      border-radius: 15px;
    }
    .vjs-playlist {
      position: absolute;
      width: 100%;
      top: 112%;
      right: 0;
      height: 100%;
      border-radius: 15px;
      background: #121212;
      counter-reset: section;
      .vjs-playlist-title-container {
        position: initial;
        width: 70%;
        padding: 0 0.8rem;
      }
      .vjs-selected img {
        opacity: 1;
      }
    }
    .vjs-playlist-duration {
      display: block;
      top: 50%;
      transform: translateY(-50%);
      right: 1rem;
      left: inherit;
      font-family: 'Open Sans', Arial, Helvetica, sans-serif;
      background: inherit;
      text-shadow: none;
      color: #4d4d4d;
    }
    .vjs-playlist-name {
      display: -webkit-box;
      font-family: 'Open Sans', Arial, Helvetica, sans-serif;
      font-weight: bold;
      padding: 0;
      color: #4d4d4d;
      text-shadow: none;
      white-space: normal;
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      box-orient: vertical;
      max-height: 44px;
    }
    .vjs-selected {
      .vjs-playlist-name {
        color: white;
      }
      .vjs-playlist-thumbnail {
        &::before {
          opacity: 1;
        }
        &::after {
          color: white;
        }
      }
      .vjs-playlist-duration {
        border-radius: 8px;
        color: white;
      }
    }
    .vjs-playlist-vertical .vjs-playlist-item {
      height: 45px;
      display: flex;
      padding:0;
      background: #121212;
      font-size: 14px;
      border-bottom: 1px solid #373737;
      margin-bottom: 0;
      .vjs-playlist-thumbnail {
        display: flex;
        align-items: center;
        padding-left:2.5rem;
        &::before {
          display: block;
          content: url('https://dkstatic.s3.amazonaws.com/Creative-Development/misc/PlayCircle.png');
          padding-right: 0.5rem;
          padding-top: 8px;
          opacity: 0.2;
        }
        &::after {
          counter-increment: section;
          content: counter(section) '.';
          font-size: 14px;
          font-weight: bold;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          left: 14px;
          color: inherit;
          color: #4d4d4d;
        }
        img {
          display: none;
        }
      }
      &.vjs-selected {
        .vjs-playlist-thumbnail {
          &::after {
            color: white;
          }
          &::before,
          img {
            opacity: 1;
          }
        }
        .vjs-playlist-name {
          color: white;
        }
        .vjs-playlist-duration {
          border-radius: 8px;
          color: white;
        }
      }
    }
    .vjs-playlist-now-playing-text,
    .vjs-up-next-text {
      display: none;
    }
  }
  .player_tag_container {
    position: absolute;
    top: 14px;
    right: 14px;
    padding: 4px 14px;
    background: #121212;
    border-radius: 12px;
    span {
      color: #6d6d6d;
      display: inline-block;
      font-weight: bold;
      letter-spacing: 3px;
    }
  }
  .video_title_container {
    display: none;
    p {
      font-size: 14px;
      font-weight: bold;
      text-align: center;
      margin-top: 1.2rem;
    }
  }
  .error_message_container {
    padding: 0 2rem;
    text-align: center;
  }
`;

const displayError = () => (
  <div className="error_message_container">
    <h5>There was an error displaying the video, please try again in a few minutes.</h5>
  </div>
);

const VideoPlaylist = ({ data }) => {
  const playlistId = data.advanced_embed_code.match(/<video-js.*?data-playlist-id="(.*?)"/);
  const playerId = data.advanced_embed_code.match(/<video-js.*?data-player="(.*?)"/);
  const accountId = data.advanced_embed_code.match(/<video-js.*?data-account="(.*?)"/);

  const playerRef = useRef({});
  const [playlistTag, setPlaylistTag] = useState('');
  const [videoTitle, setVideoPlaylist] = useState('');

  const onSuccess = async ({ ref }) => {
    playerRef.current = ref;
    ref.on('play', handlePlay);
  };

  const handlePlay = () => {
    setPlaylistTag(`${playerRef.current.playlist.currentIndex() + 1}/${playerRef.current.playlist.lastIndex() + 1}`);
  };

  useEffect(() => {
    if (playerRef.current.mediainfo) {
      setVideoPlaylist(`${playerRef.current.mediainfo.name}`);
    }
  }, [playerRef, playlistTag]);

  return (
      <StyledVideoPlayerWrapper
        globalBorder={data.global_border ? data.global_border : { bottom_border: false, top_border: false }}
      >
        {!playlistId || !accountId || !playerId ? (
          displayError()
        ) : (
          <>
            <h2 className="headline">{data.headline}</h2>
            <div className="player_container">
              <ReactPlayerLoader
                playlistId={playlistId[1]}
                playerId={playerId[1]}
                accountId={accountId[1]}
                embedOptions={{
                  playlist: true,
                  responsive: {
                    fluid: true,
                    maxWidth: '100%',
                  },
                }}
                embedType='in-page'
                onSuccess={onSuccess}
              />
              {playlistTag.length > 1 && (
                <div className="player_tag_container">
                  <span>{playlistTag}</span>
                </div>
              )}
              {videoTitle.length > 1 && (
                <div className="video_title_container">
                  <p>{videoTitle}</p>
                </div>
              )}
            </div>
          </>
        )}
      </StyledVideoPlayerWrapper>
  );
};

export default VideoPlaylist;
